/**
 * Goal progress as the web app and the MCP server show it. Not a contract function: the Swift
 * app has no equivalent rule to record cases from, so this is a thin composition of
 * contract-verified pieces (GoalTracker.calculateProgress, debt balances, check-in net worth),
 * pinned by test/plan-progress.test.ts. Both surfaces call it; neither re-derives it.
 */
import { abs, add, isZero, max, ZERO, type Decimal } from '../core/decimal.ts';
import { effectiveInitialBalance } from '../calculations/debt-projector.ts';
import { snapshotNetWorth } from '../calculations/net-worth.ts';
import { debtsCountedInNetWorth } from '../models/plan.ts';
import type { CalcContext, Checkpoint, Plan } from '../types/domain.ts';
import { calculateGoalProgress, type GoalForecast, type GoalProgress, type HistoricalBaselines } from './goal-tracker.ts';

/**
 * Reduction goals (debt free) measure progress from the most ever owed: the original balances of
 * the debts in the net-worth line, or a higher debt a checkpoint recorded. `maxNetWorth` is the
 * highest net worth any checkpoint recorded.
 */
export function planBaselines(plan: Plan, checkpoints: readonly Checkpoint[]): HistoricalBaselines {
  const originalDebt = debtsCountedInNetWorth(plan).reduce<Decimal>((sum, d) => add(sum, abs(effectiveInitialBalance(d))), ZERO);
  const maxDebt = checkpoints.map((c) => c.confirmedBalances.debt).reduce(max, originalDebt);
  return {
    maxDebt: isZero(maxDebt) ? null : maxDebt,
    maxNetWorth: checkpoints.length === 0 ? null : checkpoints.map((c) => snapshotNetWorth(c.confirmedBalances)).reduce(max),
  };
}

/** Every goal's progress, ETA and achieved flag for a plan's forecast, with `planBaselines`. */
export const planGoalProgress = (
  plan: Plan,
  forecast: GoalForecast,
  checkpoints: readonly Checkpoint[],
  context: CalcContext,
): readonly GoalProgress[] => calculateGoalProgress(plan.goals, forecast, context, { baselines: planBaselines(plan, checkpoints) });

/** The North Star: the goal with the highest priority (lowest number), first in the plan on a tie. */
export const northStar = (progress: readonly GoalProgress[]): GoalProgress | null =>
  [...progress].sort((a, b) => a.goal.priority - b.goal.priority)[0] ?? null;
